<template>
  <div class="practice-products-archive">

    <Loading :loading="isFetching" loading-text="Loading..." background="none-background">
      <div class="content-wrapper" :class="{ 'loading': isFetching }">
        <!-- Card Grid -->
        <div v-if="merchandiseItems.length > 0" class="card-grid-wrapper">
          <div class="card-grid">
            <PracticeProductCard
              v-for="item in merchandiseItems"
              :key="item.id"
              :product="adaptProductForCard(item)"
              :currency-symbol="currencySymbol"
              :sold-info="merchSoldInfo[item.id] || null"
              :loading="processingItemId === item.id"
              @restore-product="handleRestoreMerchandise"
              @delete-product="handleDeleteMerchandise"
            />
          </div>
        </div>

        <!-- Empty State -->
        <div v-else-if="!isFetching" class="card-grid-wrapper">
          <div class="no-data-state">
            <img src="@/assets/MyWorkBench/archive/empty.svg" alt="Empty" class="empty-icon" />
            <p class="empty-text">You don't have any archived practice products.</p>
          </div>
        </div>

        <!-- Pagination at bottom -->
        <Pagination
          v-if="merchandiseItems.length > 0"
          :total_items="total"
          :current_page="currentPage"
          :per_page="perPage"
          :per_page_options="[10, 20, 30]"
          @update:current_page="handlePageChange"
          @update:per_page="handlePageSizeChange"
        />
      </div>
    </Loading>

    <!-- Delete Merchandise Dialog -->
    <DeleteConfirmDialog
      v-model="deleteDialogVisible"
      modal-class="delete-confirm-dialog"
      title="Delete Practice Product"
      :item-name="selectedMerchandise?.name"
      :confirm-action="handleConfirmDelete"
      loading-text="Deleting practice product..."
      :show-icon="true"
    />
  </div>
</template>

<script>
import Loading from '@/NewLibrary/Loading/Loading.vue';
import DeleteConfirmDialog from '@/views/my-workbench-new/archive/components/DeleteConfirmDialog.vue';
import PracticeProductCard from '@/views/my-workbench-new/archive/components/PracticeProductCard.vue';
import { notify } from '@/utils/notify';
import { downloadFile } from '@/api/markup';
import {
  getArchivedMerchandisesApi,
  restoreMerchandiseApi,
  hideMerchandiseApi
} from '@/api/archive';
import { merchandiseOrderInfo } from '@/api/markup';

export default {
  name: 'PracticeProductsArchive',
  components: {
    Loading,
    DeleteConfirmDialog,
    PracticeProductCard,
  },
  props: {
    customerId: {
      type: [String, Number, null],
      default: null,
    },
    clinicId: {
      type: [String, Number, null],
      default: null,
    },
    currencySymbol: {
      type: String,
      default: '$',
    },
  },

  data() {
    return {
      isFetching: false,
      perPage: 10,
      currentPage: 1,
      merchandiseItems: [],
      total: 0,
      deleteDialogVisible: false,
      selectedMerchandise: null,
      processingItemId: null,
      merchSoldInfo: {},
      imageCache: new Map(),
    };
  },

  computed: {
    customerAndClinicId() {
      return `${this.customerId}-${this.clinicId}`;
    },
  },

  watch: {
    perPage() {
      this.fetchArchivedMerchandises();
    },
    currentPage() {
      this.fetchArchivedMerchandises();
    },
    customerAndClinicId: {
      immediate: true,
      handler() {
        if (this.customerId && this.clinicId) {
          this.fetchArchivedMerchandises();
        }
      },
    },
    merchandiseItems: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          this.fetchMerchSoldInfo();
        } else {
          this.merchSoldInfo = {};
        }
      },
    },
  },

  methods: {
    async fetchArchivedMerchandises(showLoading = true) {
      if (!this.customerId || !this.clinicId) {
        console.warn('No customer ID or clinic ID available');
        return;
      }

      if (showLoading) this.isFetching = true;
      try {
        const params = {
          customer_id: this.customerId,
          clinic_id: this.clinicId,
          page: this.currentPage,
          limit: this.perPage,
        };

        const response = await getArchivedMerchandisesApi(params);
        // Handle the API response structure
        this.merchandiseItems = response.data.merchandises || [];
        this.total = response.data.total || 0;
      } catch (error) {
        console.error('Failed to fetch archived merchandises:', error);
        this.merchandiseItems = [];
        this.total = 0;
      } finally {
        if (showLoading) this.isFetching = false;
      }
    },

    async fetchMerchSoldInfo() {
      try {
        const merchIds = this.merchandiseItems.map(item => item.id).filter(Boolean);
        if (merchIds.length === 0) return;
        
        const { data } = await merchandiseOrderInfo({ item_ids: merchIds });
        if (data) {
          this.merchSoldInfo = data;
        }
      } catch (error) {
        console.error('Error fetching merch sold info:', error);
      }
    },

    adaptProductForCard(item) {
      // Transform API response to match PracticeProductCard expected format
      let imagePath = null;
      if (item.picture) {
        // Clean up the picture path
        const cleanedPath = item.picture.replace(/[\n\r]/g, '').trim();
        if (cleanedPath) {
          imagePath = cleanedPath;
        }
      }
      
      // Format return days
      let returnDaysFormatted = 'No';
      if (item.return_and_refund_info && item.return_and_refund_info.accept_return_refund && item.return_and_refund_info.accepted_return_refund_days > 0) {
        const returnDaysNum = item.return_and_refund_info.accepted_return_refund_days;
        returnDaysFormatted = returnDaysNum > 1 ? `${returnDaysNum} Days` : `${returnDaysNum} Day`;
      }
      
      // Format refund policy
      let refundPolicy = 'No';
      if (item.return_and_refund_info && item.return_and_refund_info.accept_return_refund) {
        const deductionType = item.return_and_refund_info.merchandise_refund_deduction_type;
        const deductionValue = item.return_and_refund_info.merchandise_refund_deduction_value;
        if (deductionType === 'no_deduction') {
          refundPolicy = '100% Full';
        } else if (deductionType === 'by_percentage') {
          const percentage = (1 - deductionValue) * 100;
          const percentageStr = Number.isInteger(percentage) ? `${percentage}%` : `${percentage.toFixed(1)}%`;
          refundPolicy = percentageStr + ' Partial';
        } else if (deductionType === 'by_constant') {
          refundPolicy = this.currencySymbol + Math.max((item.price - deductionValue), 0).toFixed(2) + ' Partial';
        }
      }
      
            
      const adapted = {
        id: item.id,
        name: item.name,
        description: item.description || '',
        type: item.type,
        price: item.price ? `${this.currencySymbol}${item.price.toFixed(2)}` : `${this.currencySymbol}0.00`,
        image: imagePath,
        status: 'archived', // All archived products have archived status
        returnDays: returnDaysFormatted,
        refundPolicy: refundPolicy,
        // Provider-related fields for assignees count calculation
        apply_to_all_providers: item.apply_to_all_providers,
        apply_to_self: item.apply_to_self,
        creator_type: item.creator_type,
        // Calculate providerList same as PracticeProducts.vue parseObjectFromApi
        providerList: this.calculateProviderList(item),
        // Include original data for revenue/sold calculations
        originalData: item,
      };
      
      return adapted;
    },

    calculateProviderList(item) {
      // console.log('calculateProviderListitem', item);
      // Same logic as PracticeProducts.vue parseObjectFromApi
      const isSelf = item.apply_to_self;
      let belongToProvider = null;
      // Note: In archive context, we don't have the 'key' parameter
      // So we'll use a simplified version
      if (isSelf && item.creator_type === 'provider') {
        // In archive context, we don't have the provider key, so we'll use null
        belongToProvider = null;
      }
      
      const providerList = item.apply_merchandise_info_to
        ? item.apply_merchandise_info_to
        : isSelf
          ? belongToProvider ? [belongToProvider] : []
          : [];
      // console.log('calculateProviderListitem,providerList', providerList);
      return providerList;
    },

    async getImageSrc(imagePath) {
      if (!imagePath) return null;
      
      // Check cache first
      if (this.imageCache.has(imagePath)) {
        return this.imageCache.get(imagePath);
      }

      try {
        const parts = imagePath.split('/');
        const folderName = parts[0];
        const fileName = parts[parts.length - 1];
        const { data } = await downloadFile(folderName, fileName);
        const url = URL.createObjectURL(data);
        this.imageCache.set(imagePath, url);
        return url;
      } catch (error) {
        console.error('Failed to load image:', error);
        return null;
      }
    },

    async handleRestoreMerchandise(item) {
      if (this.processingItemId) return;
      try {
        this.processingItemId = item.id;
        await restoreMerchandiseApi(item.id);
        notify({ type: 'success', message: `"${item.name}" has been restored` });
        await this.fetchArchivedMerchandises(false);
      } catch (error) {
        console.error('Failed to restore merchandise:', error);
        notify({ type: 'error', message: `Failed to restore "${item.name}"` });
      } finally {
        this.processingItemId = null;
      }
    },

    handleDeleteMerchandise(item) {
      this.selectedMerchandise = item;
      this.deleteDialogVisible = true;
    },

    async handleConfirmDelete() {
      if (!this.selectedMerchandise) return;
      if (this.processingItemId) return;
      const deletingMerchandise = this.selectedMerchandise;
      this.processingItemId = deletingMerchandise.id;
      try {
        await hideMerchandiseApi(deletingMerchandise.id);
        notify({
          type: 'success',
          title: 'Practice Product Deleted',
          message: `"${deletingMerchandise.name}" has been permanently deleted.`
        });
        await this.fetchArchivedMerchandises(false);
      } catch (error) {
        console.error('Failed to hide merchandise:', error);
        notify({
          type: 'error',
          title: 'Error',
          message: 'Failed to delete practice product. Please try again.'
        });
        throw error;
      } finally {
        this.processingItemId = null;
        this.selectedMerchandise = null;
      }
    },

    formatType(type) {
      if (!type) return '-';
      return type.charAt(0).toUpperCase() + type.slice(1);
    },

    handlePageChange(page) {
      this.currentPage = page;
    },

    handlePageSizeChange(size) {
      this.perPage = size;
      this.currentPage = 1;
    },
  },
};
</script>

<style scoped>
.practice-products-archive {
  padding: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  overflow: hidden;
  position: relative;
}

.content-wrapper {
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}


@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.content-wrapper.loading {
  opacity: 0.3;
  pointer-events: none;
}

/* Card Grid Wrapper - similar to table-wrapper */
.card-grid-wrapper {
  flex: 1;
  min-height: 0;
  overflow: auto;
  display: flex;
  flex-direction: column;
}

.card-grid-wrapper::-webkit-scrollbar {
  width: 6px;
}

.card-grid-wrapper::-webkit-scrollbar-track {
  background: transparent;
}

.card-grid-wrapper::-webkit-scrollbar-thumb {
  background: #C4C4C4;
  border-radius: 3px;
}

.card-grid-wrapper::-webkit-scrollbar-thumb:hover {
  background: #999;
}

/* Card Grid */
.card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  grid-auto-rows: max-content;
  gap: 24px;
  padding: 8px 0 16px;
  min-height: 200px;
  align-items: start;
  align-content: start;
}

.card-grid::-webkit-scrollbar {
  width: 6px;
}

.card-grid::-webkit-scrollbar-track {
  background: transparent;
}

.card-grid::-webkit-scrollbar-thumb {
  background: #C4C4C4;
  border-radius: 3px;
}

.card-grid::-webkit-scrollbar-thumb:hover {
  background: #999;
}

/* No Data State - matching ProductBundleArchive */
.no-data-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  flex: 1;
  min-height: 100%;
  width: 100%;
  text-align: center;
}

.empty-icon {
  width: 350px;
  height: 230px;
}

.empty-text {
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: #333;
  margin: 0;
}

:deep(.medication-pagination) {
  flex-shrink: 0;
}
</style>
